"use client";

import React, { useEffect, useState, useRef } from "react";
import type { ProductDetails } from "@/lib/centra";
import { addLeadingSlash } from "@frend-digital/utils";
import styles from "./Reviews.module.css";
import { clsx } from "@frend-digital/ui";

export const RatingStars = ({ product, slug }: { product: ProductDetails; slug: string[] }) => {
	const [hideWidget, setHideWidget] = useState(false);
	const ratingDivRef = useRef<HTMLDivElement>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		const el = ratingDivRef.current;
		if (!el) return;

		const checkVotes = () => {
			const raw = el.dataset.lsProductVotes;
			if (raw === undefined) return; // Lipscore hasn't initialized yet — don't hide prematurely
			const votes = Number.parseInt(raw, 10);
			if (!Number.isNaN(votes) && votes === 0) {
				setHideWidget(true);
			}
		};

		const observer = new MutationObserver((mutationsList) => {
			for (const mutation of mutationsList) {
				if (mutation.attributeName === "data-ls-product-votes") {
					checkVotes();
				}
			}
		});

		observer.observe(el, {
			attributes: true,
			attributeFilter: ["data-ls-product-votes"],
		});

		const timeout = setTimeout(checkVotes, 300);

		return () => {
			observer.disconnect();
			clearTimeout(timeout);
		};
	}, [product.sku]);

	if (hideWidget) return null;
	return (
		// biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
		<div
			style={{ minHeight: 24 }}
			onClick={() => {
				const id = "lipscore-review-list";
				const details = document.getElementById(id)?.closest("details");

				if (details) {
					details.open = true;
					setTimeout(() => {
						document.getElementById(id)?.scrollIntoView({
							behavior: "smooth",
							block: "start",
						});
					}, 32);
				}
			}}
			className={clsx(styles.ratingStarsLink)}
		>
			<div
				ref={ratingDivRef}
				id="lipscore-rating"
				data-ls-product-name={product.name}
				data-ls-brand="Viking"
				data-ls-product-id={product.centraProductNumber}
				data-ls-product-url={`https://vikingfootwear.com${addLeadingSlash(slug.join("/"))}`}
				data-ls-variant-id={product.displayItemId}
				data-ls-variant-name={product.variantName}
				data-ls-sku={product.sku}
				data-ls-description={product.shortDescription}
				data-ls-image-url={product.media.at(0)?.href}
				data-ls-price={product.priceAsNumber}
				data-ls-price-currency={product.price?.match(/[A-Z]{3}$/)?.[0]}
				data-ls-category={product.canonicalUri}
			/>
		</div>
	);
};
